<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'

const props = defineProps({
  leagueId: {
    type: [String, Number],
    required: true
  },
  teamId: {
    type: [String, Number],
    required: true
  },
  awayTeamId: {
    type: [String, Number],
    required: false,
    default: null
  }
})

// Component state
const loading = ref(false)
const error = ref(null)
const depthChartColumns = ref(null)
const homeTeamData = ref(null)
const awayTeamData = ref(null)

// Process depth chart data for home team
const processedHomeTeamData = computed(() => {
  if (!depthChartColumns.value || !Array.isArray(depthChartColumns.value)) {
    return []
  }
  
  // Filter for home team data and sort by role_order
  return depthChartColumns.value
    .filter(item => item.team_id === props.teamId)
    .sort((a, b) => a.role_order - b.role_order)
})

// Process depth chart data for away team (assuming we have both teams' data)
const processedAwayTeamData = computed(() => {
  if (!depthChartColumns.value || !Array.isArray(depthChartColumns.value)) {
    return []
  }
  
  // If awayTeamId is provided, filter for that team
  if (props.awayTeamId) {
    return depthChartColumns.value
      .filter(item => item.team_id === props.awayTeamId)
      .sort((a, b) => a.role_order - b.role_order)
  }
  
  // If no awayTeamId provided, find a different team_id from the data
  const uniqueTeamIds = [...new Set(depthChartColumns.value.map(item => item.team_id))]
  const awayTeamId = uniqueTeamIds.find(id => id !== props.teamId)
  
  if (!awayTeamId) return []
  
  return depthChartColumns.value
    .filter(item => item.team_id === awayTeamId)
    .sort((a, b) => a.role_order - b.role_order)
})

// Helper function to get player name from player object or ID
const getPlayerName = (playerData) => {
  // If it's a player object with name
  if (playerData && typeof playerData === 'object' && playerData.player_name) {
    return playerData.player_name
  }
  
  // If it's just an ID string
  if (playerData && playerData !== 'null' && playerData !== '') {
    return `Player ${playerData}`
  }
  
  // Empty or null
  return 'Click to select'
}

// Role background colors
const getRoleBackgroundColor = (role) => {
  const colors = {
    'Starter': 'bg-green-50',
    'Rotational Player': 'bg-blue-50', 
    'Injury Backup': 'bg-yellow-50',
    'Injury Backup 2': 'bg-yellow-50',
    'Injury Backup 3': 'bg-yellow-50',
    'In & Out of Rotation': 'bg-orange-50',
    'Out of Rotation': 'bg-red-50'
  }
  return colors[role] || 'bg-gray-50'
}

// Fetch depth chart columns on mount
const fetchDepthChartColumns = async () => {
  if (!props.leagueId || !props.teamId) {
    error.value = 'League ID and Team ID are required'
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await axios.get('api/v2/projections-settings/teams/depth-chart-columns', {
      params: {
        league_id: props.leagueId,
        team_id: props.teamId
      }
    })
    
    depthChartColumns.value = response.data
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Failed to fetch depth chart columns'
    console.error('Error fetching depth chart columns:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDepthChartColumns()
})
</script>

<template>
  <div class="space-y-4">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="flex items-center gap-3">
        <i class="fas fa-spinner fa-spin text-blue-500"></i>
        <span class="text-gray-600">Loading depth chart columns...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center gap-3">
        <i class="fas fa-exclamation-triangle text-red-500"></i>
        <div>
          <h4 class="text-red-800 font-medium">Error Loading Depth Chart Columns</h4>
          <p class="text-red-600 text-sm mt-1">{{ error }}</p>
        </div>
      </div>
      <button 
        @click="fetchDepthChartColumns" 
        class="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
      >
        <i class="fas fa-redo mr-2"></i>
        Retry
      </button>
    </div>

    <!-- Success State -->
    <div v-else-if="depthChartColumns" class="space-y-6">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center gap-3">
          <i class="fas fa-table text-blue-500"></i>
          <div>
            <h4 class="text-blue-800 font-medium">Team Depth Chart</h4>
            <p class="text-blue-600 text-sm mt-1">
              Found {{ Array.isArray(depthChartColumns) ? depthChartColumns.length : Object.keys(depthChartColumns).length }} depth chart entries
            </p>
          </div>
        </div>
      </div>

      <!-- Home Team Depth Chart -->
      <div class="bg-white rounded-lg border overflow-hidden">
        <div class="p-4 border-b bg-gray-50">
          <h5 class="font-medium text-gray-800 flex items-center gap-2">
            <i class="fas fa-home"></i>
            Home Team Depth Chart
          </h5>
        </div>
        
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PG</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">SG</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">SF</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PF</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">C</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="roleData in processedHomeTeamData" :key="`home-${roleData.id}`" 
                  :class="getRoleBackgroundColor(roleData.role)" class="hover:bg-opacity-80">
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ roleData.role }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.pg_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.sg_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.sf_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.pf_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.c_player) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Away Team Depth Chart -->
      <div class="bg-white rounded-lg border overflow-hidden">
        <div class="p-4 border-b bg-gray-50">
          <h5 class="font-medium text-gray-800 flex items-center gap-2">
            <i class="fas fa-away"></i>
            Away Team Depth Chart
          </h5>
        </div>
        
        <div v-if="processedAwayTeamData.length > 0" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PG</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">SG</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">SF</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PF</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">C</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="roleData in processedAwayTeamData" :key="`away-${roleData.id}`" 
                  :class="getRoleBackgroundColor(roleData.role)" class="hover:bg-opacity-80">
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ roleData.role }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.pg_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.sg_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.sf_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.pf_player) }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">{{ getPlayerName(roleData.c_player) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- No Away Team Data Message -->
        <div v-else class="p-8 text-center">
          <i class="fas fa-info-circle text-gray-400 text-3xl mb-3"></i>
          <p class="text-gray-600 text-sm">No away team data available</p>
          <p class="text-gray-500 text-xs mt-1">Make sure to provide awayTeamId prop or include away team data in the API response</p>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-8">
      <i class="fas fa-info-circle text-gray-400 text-4xl mb-4"></i>
      <p class="text-gray-600">No depth chart columns data available</p>
    </div>
  </div>
</template>

<style scoped>
/* Additional custom styles if needed */
</style>


